// TextPrompt.tsx · v12.10.31 (#2296 #2286 the "Text prompt" line under every Preview + the card's Text switch - T224.18 LOCKED 11/4) · 2026-11-05 · VoiceSpectrum AI
// Rules (backlog #2296, Tushar 11/4 7:30-9:15 PM):
//  · Shown ONLY when the Text messaging workflow (Text workspace) is on. Off = no Text switches, no Text prompts anywhere (the stored wordings are
//    kept - hidden, never deleted).
//  · Sits right under the call prompt's Preview, per language (follows the language rail). Empty = the text sends the call prompt.
//  · Read-back line format, tinted with the text colour. Filled + in use -> expanded. "Use the call prompt" -> the wording is KEPT (stored, not
//    used) and the line collapses to the call prompt; "Add text wording" reopens the stored text. No Undo, no confirm.
//  · A filled Text prompt notes when the CALL prompt changed after it was written ("Still fits" clears it).
//  · The card's Text switch (default On; P9 terminals: End call / Transfer to staff / Route to operator default Off; TextConfirm Off + locked).
//    Off = the card is skipped on text and its Text prompt lines are hidden - the call side looks exactly the same (not greyed).
// Storage (BUILD-12.10.31-CONTRACTS.md ## F2): doc.textPrompts[<cardKey>.<field>] = { byLang, inUse, callText }; doc.textCards[<cardKey>] = bool.
import { createContext, useContext } from 'react';

export interface TextPromptEntry { byLang?: Record<string, string>; inUse?: boolean; callText?: Record<string, string>; callChangedAt?: string }
export interface TextPromptStore {
  enabled: boolean;                              // the Text workspace is on
  cardKey: string | null;                        // the open card ('' = none)
  cardOn: boolean;                               // that card's Text switch
  cardLocked?: boolean;
  get: (key: string) => TextPromptEntry | undefined;
  set: (key: string, entry: TextPromptEntry | undefined) => void;
  setCard: (on: boolean) => void;
}
export const TextPromptContext = createContext<TextPromptStore | null>(null);

/** Template defaults for a card's Text switch (P9 / P8 / P10). Absent from doc.textCards = this default. */
export function defaultCardTextOn(cardKey: string): { on: boolean; locked?: boolean } {
  if (/^terminal:(endcall|end-call|end_call|hangup|transfer|handoff|operator|route-to-operator|routeToOperator)$/i.test(cardKey)) return { on: false };
  if (/textconfirm/i.test(cardKey)) return { on: false, locked: true };
  if (/^interceptor:(sayAgain|say-again|repeat|transfer|transferOrEnd|terminalKeywords|changeOfIntent)/i.test(cardKey)) return { on: false };
  if (/^preRouting:(languageDetect|language)/i.test(cardKey)) return { on: false, locked: true };   // P7: Language detect is call-only
  if (/^readbacks/i.test(cardKey)) return { on: false, locked: true };                                // P6: no read-backs on text
  return { on: true };
}

export function TextPromptLine({ field, language, callText }: { field?: string; language: string; callText: string }) {
  const ctx = useContext(TextPromptContext);
  if (!ctx || !ctx.enabled || !ctx.cardKey || !field || !ctx.cardOn) return null;
  const key = `${ctx.cardKey}.${field}`;
  const entry = ctx.get(key) ?? {};
  const text = entry.byLang?.[language] ?? '';
  const open = !!entry.inUse;
  const changed = open && !!text.trim() && entry.callText && entry.callText[language] !== undefined && (entry.callText[language] ?? '') !== callText;
  const write = (p: Partial<TextPromptEntry>) => ctx.set(key, { ...entry, ...p });
  const setText = (v: string) => write({ byLang: { ...(entry.byLang ?? {}), [language]: v }, callText: { ...(entry.callText ?? {}), [language]: callText } });
  return (
    <div className={`wf-textprompt${open ? ' is-open' : ''}`} data-testid="text-prompt-line">
      <div className="wf-textprompt-head">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
        <b>Text prompt</b>
        {!open && <span className="wf-textprompt-same">same as the call prompt{callText.trim() ? <> · “{callText.length > 90 ? callText.slice(0, 90) + '…' : callText}”</> : null}</span>}
        {open && <span className="wf-textprompt-same">· {language.toUpperCase()} · sent instead of the call prompt when this runs by text</span>}
        {!open
          ? <button type="button" className="wf-textprompt-link" onClick={() => write({ inUse: true })}>{(entry.byLang && Object.values(entry.byLang).some((t) => (t ?? '').trim())) ? 'Add text wording (kept)' : 'Add text wording'}</button>
          : <button type="button" className="wf-textprompt-link" onClick={() => write({ inUse: false })} title="Keeps the wording (not used); the text sends the call prompt">Use the call prompt</button>}
      </div>
      {open && (
        <>
          <textarea className="wf-textprompt-box" rows={2} value={text} placeholder={callText} aria-label={`Text prompt ${language.toUpperCase()}`}
            onChange={(e) => setText(e.target.value)} />
          {!text.trim() && <small className="wf-textprompt-note">Empty = the text sends the call prompt.</small>}
          {changed && (
            <small className="wf-textprompt-note is-warn">The call prompt changed since this was written - check the text wording still fits.{' '}
              <button type="button" className="wf-textprompt-link" onClick={() => write({ callText: { ...(entry.callText ?? {}), [language]: callText } })}>Still fits</button>
            </small>
          )}
        </>
      )}
    </div>
  );
}

/** The card's Text switch - in the card's first section header row (best effort: a slim row at the top of the drawer content). */
export function CardTextSwitch() {
  const ctx = useContext(TextPromptContext);
  if (!ctx || !ctx.enabled || !ctx.cardKey) return null;
  return (
    <div className="wf-cardtext" data-testid="card-text-switch">
      <label className={`wf-cardtext-sw${ctx.cardOn ? ' on' : ''}${ctx.cardLocked ? ' is-locked' : ''}`}
        title={ctx.cardLocked ? 'This card never runs on text' : ctx.cardOn ? 'On - this card runs in a text conversation' : 'Off - this card is skipped in a text conversation'}>
        <span className="wf-cardtext-cap">Text</span>
        <input type="checkbox" checked={ctx.cardOn} disabled={ctx.cardLocked} onChange={(e) => ctx.setCard(e.target.checked)} />
        <i />
      </label>
    </div>
  );
}
